import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Switch,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Grid,
  Box,
  Typography,
  Divider,
  AppBar,
  Button,
  TextField,
  Toolbar,
  IconButton,
  Paper,
  CircularProgress,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Checkbox,
  ListItemText,
} from '@material-ui/core';
import { Close as IconClose } from '@material-ui/icons';
import * as pdvActions from '~/store/modules/pdv/pdv-actions';
import { updatePreferencias, resetPreferencias } from '~/store/modules/preferencias/preferencias-actions';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { PREFERENCIAS_DEFAULTS, PREFERENCIAS_INPUT_TYPE, PREFERENCIAS_CATEGORIAS } from '~/store/modules/preferencias/preferencias-defaults';
import { makeStyles } from '@material-ui/core/styles';
import { TIPO_DOCUMENTO } from '~/constants';
import print from '~/services/print';
import { selectorParametroAsBoolean } from '~/store/modules/auth/auth-selectors';
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep';

const useStyles = makeStyles(theme => ({
  categoryTitleLabel: {
    fontSize: theme.typography.subtitle1.fontSize,
    fontWeight: 600,
    lineHeight: theme.typography.subtitle1.lineHeight,
    color: theme.palette.text.primary,
  },

  categoryDescriptionLabel: {
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.caption.fontWeight,
    lineHeight: theme.typography.caption.lineHeight,
    color: theme.palette.text.secondary,
  },

  toggleInputRoot: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: 0,
  },

  toggleInputLabelTitle: {
    flex: 1,
    marginRight: 8,
    fontSize: theme.typography.body2.fontSize,
    fontWeight: 500,
    lineHeight: 1.4,
    color: theme.palette.text.primary,
  },

  toggleInputLabelDescription: {
    flex: 1,
    marginRight: 8,
    fontSize: theme.typography.caption.fontSize,
    lineHeight: 1.3,
    color: theme.palette.text.secondary,
    marginTop: 2,
  },

  selectInputLabelTitle: {
    fontSize: theme.typography.body2.fontSize,
    fontWeight: 500,
    lineHeight: 1.4,
    color: theme.palette.text.primary,
  },

  selectInputLabelDescription: {
    fontSize: theme.typography.caption.fontSize,
    lineHeight: 1.3,
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
}));


const ModalPreferencias = () => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const { usuario: usuarioLogado } = useSelector(state => state.auth);
  const { tipoDocumento } = useSelector(state => state.pdv);

  const [loadingReset, setLoadingReset] = useState(false);

  const parametroPC009 = useSelector(selectorParametroAsBoolean('PC009'))

  const { showModalPreferencias } = useSelector(state => state.pdv);
  const { preferencias, loaded } = useSelector(state => state.preferencias);

  const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

  const handleModalClose = () => {
    dispatch(pdvActions.setShowModalPreferencias({ show: false }));
  };

  const handleLimparPreferencias = async () => {
    setLoadingReset(true)
    
    await print.savePreference(usuarioLogado?.id, { tipoDocumento, ...{
        servidor: '',
        porta: '',
        utilizaServidorImpressao: false,
        imprimirAposSalvar: false,
        imprimirExpedicao: []
      } }, false);
    
    dispatch(resetPreferencias());

    await delay(1000)

    setLoadingReset(false)
  };

  const handlePreferenciasChange = (key, value) => {
    dispatch(updatePreferencias(key, value));
  };

  return (
    <Dialog
      open={showModalPreferencias}
      onKeyDown={e => e.code === 'Escape' && handleModalClose()}
      PaperComponent={Paper}
      maxWidth="md"
    >
      <DialogTitle disableTypography>
        <AppBar color="primary" position="static">
          <Toolbar>
            <Typography variant="h5" style={{ textTransform: 'uppercase' }}>
              Preferências
            </Typography>

            <Box flexGrow={1} />

            <Tooltip title="Limpar preferências">
              <IconButton color="inherit" onClick={handleLimparPreferencias}>
                <DeleteSweepIcon />
              </IconButton>
            </Tooltip>

            <IconButton color="inherit" onClick={handleModalClose}>
              <IconClose />
            </IconButton>
          </Toolbar>
        </AppBar>
      </DialogTitle>

      <DialogContent style={{ minWidth: 320, minHeight: 80, padding: '12px 8px', alignContent: 'center' }}>
        {(!loaded || loadingReset) ?
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            minWidth={320}
            minHeight={80}
            padding={4}
          >
            <CircularProgress />
          </Box>
        :
          <React.Fragment>
            {PREFERENCIAS_CATEGORIAS.length > 0 ? (
              <React.Fragment>
                {PREFERENCIAS_CATEGORIAS.map((categoria, categoriaIdx) => {
                  const preferenciasDaCategoria = PREFERENCIAS_DEFAULTS.filter(
                    (pref) => pref.categoriaKey === categoria.key
                  );

                  if (preferenciasDaCategoria.length === 0) return null;

                  return (
                    <React.Fragment key={categoria.key}>
                      {categoriaIdx !== 0 && <Divider />}
                      <Accordion>

                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box style={{ display: 'flex', flexDirection: 'column' }}>
                            {categoria.title && (
                              <Typography className={classes.categoryTitleLabel}>
                                {categoria.title}
                              </Typography>
                            )}

                            {categoria.description && (
                              <Typography className={classes.categoryDescriptionLabel}>
                                {categoria.description}
                              </Typography>
                            )}
                          </Box>
                        </AccordionSummary>

                        <AccordionDetails style={{ display: 'block', width: '100%' }}>
                          <List style={{ width: '100%' }}>

                            {categoria.key === 'impressao' && (
                              <ExtrasCategoriaImpressao />
                            )}

                            {preferenciasDaCategoria.map((preferencia, preferenciaIdx) => {

                              if ( !parametroPC009 && (preferencia.key === 'impressaoVenda') ) {
                                const optionsSemAmbiente = preferencia.options.filter(
                                  opt => opt.value !== 'AMBIENTE'
                                );
                                if (optionsSemAmbiente?.length === 1 && optionsSemAmbiente[0]?.value === 'PADRAO') {
                                  return
                                }
                              }

                              return (
                                <>
                                  <React.Fragment key={preferencia.key}>
                                    {categoria.key === 'impressao' ?
                                      <Divider />
                                    :
                                      (preferenciaIdx !== 0 && <Divider />)
                                    }
                                    <PreferenciaItem
                                      pref={preferencia}
                                      value={preferencias[preferencia.key]}
                                      onChange={handlePreferenciasChange}
                                    />
                                  </React.Fragment>
                                </>
                              )
                            })}
                            
                          </List>
                        </AccordionDetails>

                      </Accordion>
                    </React.Fragment>
                  )
                })}
              </React.Fragment>
            ) : (
              <Typography className={classes.toggleInputLabelTitle} style={{ minHeight: '50px', fontSize: '16px', alignContent: 'center', textAlign: 'center', margin: '6px' }}>
                Nenhuma opção disponível
              </Typography>
            )}
          </React.Fragment>
        }
      </DialogContent>
    </Dialog>
  );
};


const PreferenciaItem = ({ pref, value, onChange }) => {
  const classes = useStyles();

  switch (pref.type) {

    case PREFERENCIAS_INPUT_TYPE.TOGGLE:
     return(
      <ListItem button style={{ width: '100%' }} >
        <FormControlLabel
          classes={{
            root: classes.toggleInputRoot,
            label: classes.toggleInputLabel,
          }}
          labelPlacement="start"
          label={
            <Box style={{ display: 'flex', flexDirection: 'column' }}>
              {pref.title && (
                <Typography className={classes.toggleInputLabelTitle}>
                  {pref.title}
                </Typography>
              )}

              {pref.description && (
                <Typography className={classes.toggleInputLabelDescription}>
                  {pref.description}
                </Typography>
              )}
            </Box>
          }
          control={
            <Switch
              checked={Boolean(value)}
              onChange={e => onChange(pref.key, e.target.checked)}
              color="primary"
            />
          }
        />
      </ListItem>
    )


    case PREFERENCIAS_INPUT_TYPE.SELECT:
      return (
        <ListItem
          style={{ width: '100%' }}
        >
          <Grid container alignItems="center" style={{ width: '100%', gap: '4px' }}>
            <Grid item xs container direction="column">
              {pref.title && (
                <Typography className={classes.selectInputLabelTitle}>
                  {pref.title}
                </Typography>
              )}

              {pref.description && (
                <Typography className={classes.selectInputLabelDescription}>
                  {pref.description}
                </Typography>
              )}
            </Grid>
            <Grid item>
              <Typography fullWidth component="div" style={{textOverflow: 'ellipsis'}}>
                <Select
                  fullWidth
                  style={{
                    height: 44,
                    minWidth: '100px',
                    maxWidth: '300px',
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: '300px',
                        maxWidth: '300px',
                        overflowX: 'ellipsis'
                      }
                    }
                  }}
                  variant="outlined"
                  value={value ?? ''}
                  onChange={e => onChange(pref.key, e.target.value)}
                >
                  {pref.options.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </Typography>
            </Grid>
          </Grid>
        </ListItem>
      );

      case PREFERENCIAS_INPUT_TYPE.TEXT:
        return (
          <ListItem
            style={{ width: '100%' }}
          >
            <Grid container alignItems="center" style={{ width: '100%', gap: '4px' }}>
              <Grid item xs container direction="column">
                {pref.title && (
                  <Typography className={classes.selectInputLabelTitle}>
                    {pref.title}
                  </Typography>
                )}
                {pref.description && (
                  <Typography className={classes.selectInputLabelDescription}>
                    {pref.description}
                  </Typography>
                )}
              </Grid>
              <Grid item>
                <TextField
                  id="outlined-size-small"
                  label={pref.placeholder ?? ''}
                  value={value ?? ''}
                  onChange={e => onChange(pref.key, e.target.value)}
                  variant="outlined"
                  fullWidth
                  size="small"
                  style={{
                    minWidth: 0,
                    width: '100%',
                    maxWidth: '100%',
                  }}
                />
              </Grid>
            </Grid>
          </ListItem>
        )

      case PREFERENCIAS_INPUT_TYPE.NUMBER:
        return (
          <ListItem
            style={{ width: '100%' }}
          >
            <Grid container alignItems="center" style={{ width: '100%', gap: '4px' }}>
              <Grid item xs container direction="column">
                {pref.title && (
                  <Typography className={classes.selectInputLabelTitle}>
                    {pref.title}
                  </Typography>
                )}
                {pref.description && (
                  <Typography className={classes.selectInputLabelDescription}>
                    {pref.description}
                  </Typography>
                )}
              </Grid>
              <Grid item>
                <TextField
                  id="outlined-size-small"
                  label={pref.placeholder ?? ''}
                  type='number'
                  value={value ?? ''}
                  onChange={e =>
                    onChange(
                      pref.key,
                      e.target.value === '' ? null : Number(e.target.value)
                    )
                  }
                  variant="outlined"
                  fullWidth
                  size="small"
                  style={{
                    minWidth: 0,
                    width: '100%',
                    maxWidth: '100%',
                  }}
                />
              </Grid>
            </Grid>
          </ListItem>
        )


    default:
      return null;


  }
};

function ExtrasCategoriaImpressao() {
  const classes = useStyles();

  const { tipoDocumento } = useSelector(state => state.pdv);
  const { usuario: usuarioLogado } = useSelector(state => state.auth);
  const [preferenciaUsuario, setPreferenciaUsuario] = useState({
    servidor: '',
    porta: '',
    utilizaServidorImpressao: false,
    imprimirAposSalvar: false,
    imprimirExpedicao: []
  });
  const [servidorPortaInput, setServidorPortaInput] = useState({
    servidor: '',
    porta: ''
  });
  const [showOpcoesExpedicao, setShowOpcoesExpedicao] = useState(false);

  const opcoesImprimirExpedicao = [
    { value: '01', label: 'NF (01)' },
    { value: '65', label: 'NFCE (65)' },
  ];

  const handlePreferenciaUsuario = async ( updatedState, displayToast = true ) => {
    await print.savePreference(usuarioLogado?.id, { tipoDocumento, ...updatedState }, displayToast);
  };

  useEffect(() => {
    getStoredUserPrintPrefs()
  }, [tipoDocumento]);

  function getStoredUserPrintPrefs() {
    const usuarioId = usuarioLogado?.id;
    const _preferencia = print.getPreference(usuarioId, tipoDocumento);
    if (_preferencia) {
      setPreferenciaUsuario(_preferencia);
      setServidorPortaInput({
        servidor: _preferencia.servidor ?? '',
        porta: _preferencia.porta ?? ''
      })
      setShowOpcoesExpedicao(!!_preferencia.imprimirExpedicao?.length);
    }
  }

  return (

    <React.Fragment>

      <ListItem button style={{ width: '100%' }}>
        <FormControlLabel
          classes={{
            root: classes.toggleInputRoot,
            label: classes.toggleInputLabel,
          }}
          labelPlacement="start"
          label={
            <Box style={{ display: 'flex', flexDirection: 'column' }}>
              <Typography className={classes.toggleInputLabelTitle}>
                Imprimir após salvar?
              </Typography>
            </Box>
          }
          control={
            <Switch
              checked={Boolean(preferenciaUsuario?.imprimirAposSalvar)}
              onChange={e => {
                setPreferenciaUsuario(state => {
                  const updatedState = {
                    ...state,
                    imprimirAposSalvar: e.target.checked
                  }
                  handlePreferenciaUsuario(updatedState, false);
                  return updatedState
                })
              }}
              color="primary"
            />
          }
        />
      </ListItem>

      {tipoDocumento === TIPO_DOCUMENTO.DOCUMENTOSAIDA && (
        <>
          <Divider />
          <ListItem button style={{ width: '100%' }}>
            <FormControlLabel
              classes={{
                root: classes.toggleInputRoot,
                label: classes.toggleInputLabel,
              }}
              labelPlacement="start"
              label={
                <Box style={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography className={classes.toggleInputLabelTitle}>
                    Imprimir Expedição?
                  </Typography>
                </Box>
              }
              control={
                <Switch
                  checked={showOpcoesExpedicao}
                  onChange={(event) => {
                    setShowOpcoesExpedicao(event.target.checked);
                    setPreferenciaUsuario(state => {
                      const updatedState = {
                        ...state,
                        imprimirExpedicao: event.target.checked ? ['01', '65'] : []
                      }
                      handlePreferenciaUsuario(updatedState, false);
                      return updatedState
                    })
                  }}
                  color="primary"
                />
              }
            />
          </ListItem>

          {showOpcoesExpedicao && (
            <ListItem style={{ width: '100%' }}>
              <Select
                fullWidth
                style={{ height: 44 }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: '300px',
                      maxWidth: '300px',
                      overflowX: 'ellipsis'
                    },
                  },
                }}
                variant="outlined"
                multiple
                displayEmpty
                value={Array.isArray(preferenciaUsuario?.imprimirExpedicao)
                  ? preferenciaUsuario.imprimirExpedicao
                  : []
                }
                onChange={e => {
                  const value = e.target.value;

                  setPreferenciaUsuario(state => {
                    const updatedState = {
                      ...state,
                      imprimirExpedicao: value
                    };
                    handlePreferenciaUsuario(updatedState, false);
                    return updatedState;
                  });

                  setShowOpcoesExpedicao(!!value.length);
                }}
                renderValue={(selected) => {
                  if (!selected.length) {
                    return <Typography>-</Typography>;
                  }
                  return [...selected].sort().join(' | ');
                }}
              >
                {opcoesImprimirExpedicao.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Checkbox
                      checked={(preferenciaUsuario?.imprimirExpedicao || []).includes(opt.value)}
                    />
                    <ListItemText primary={opt.label} />
                  </MenuItem>
                ))}
              </Select>
            </ListItem>
          )}
        </>
      )}

      <Divider />

      <ListItem button style={{ width: '100%' }}>
        <FormControlLabel
          classes={{
            root: classes.toggleInputRoot,
            label: classes.toggleInputLabel,
          }}
          labelPlacement="start"
          label={
            <Box style={{ display: 'flex', flexDirection: 'column' }}>
              <Typography className={classes.toggleInputLabelTitle}>
                Utilizar servidor de impressão?
              </Typography>
            </Box>
          }
          control={
            <Switch
              checked={Boolean(preferenciaUsuario?.utilizaServidorImpressao)}
              onChange={e => {
                setPreferenciaUsuario(state => {
                  const updatedState = {
                    ...state,
                    utilizaServidorImpressao: e.target.checked
                  }
                  handlePreferenciaUsuario(updatedState, false);
                  return updatedState
                })
              }}
              color="primary"
            />
          }
        />
      </ListItem>

      {preferenciaUsuario?.utilizaServidorImpressao && (
        <>
          <Divider />
          <Box p={2}>
            <Grid container item style={{ display: 'block', width: '100%' }}>
              <Grid item xs={12}>
                <TextField
                  id="outlined-size-small"
                  label="Servidor"
                  value={servidorPortaInput?.servidor ?? ''}
                  onChange={e =>
                    setServidorPortaInput(state => ({
                      ...state,
                      servidor: e.target.value
                    }))
                  }
                  variant="outlined"
                  fullWidth
                  size="small"
                  style={{
                    minWidth: 0,
                    width: '100%',
                    maxWidth: '100%',
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="outlined-size-small"
                  label="Porta"
                  value={servidorPortaInput?.porta ?? ''}
                  onChange={e =>
                    setServidorPortaInput(state => ({
                      ...state,
                      porta: e.target.value
                    }))
                  }
                  variant="outlined"
                  fullWidth
                  size="small"
                  style={{
                    marginTop: 6,
                    minWidth: 0,
                    width: '100%',
                    maxWidth: '100%',
                  }}
                />
              </Grid>
            </Grid>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              style={{ marginTop: 8 }}
              onClick={() =>
                setPreferenciaUsuario(state => {
                  const updatedState = {
                    ...state,
                    servidor: servidorPortaInput?.servidor ?? '',
                    porta: servidorPortaInput?.porta ?? ''
                  }
                  handlePreferenciaUsuario(updatedState, true);
                  return updatedState
                })
              }
            >
              Salvar e testar
            </Button>
          </Box>
        </>
      )}

    </React.Fragment>

  );
}



export default ModalPreferencias;
